import React, { useState, useRef } from "react";
import { useAttractions } from "../contexts/AttractionsContext";
import { useLanguage } from "../contexts/LanguageProvider";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { MapPin, Clock, Phone, Star, Navigation, Utensils, Mountain, Building2 } from "lucide-react";
import { ACCESSIBILITY_OVERLAYS_ENABLED } from "../config/constants";
import anoViannosBackground from "../assets/ano-viannos-background.jpg";
import InteractiveMap from "../components/InteractiveMap";

export default function LocalArea() {
  const { attractions, loading, error } = useAttractions();
  const { t, language } = useLanguage();
  const [activeCategory, setActiveCategory] = useState("all");
  const attractionsRef = useRef(null);


  // Note: Image preloading removed as cards no longer display images

  const categories = [
    { value: "all", labelKey: "categoryAll", icon: Star },
    { value: "beach", labelKey: "categoryBeaches", icon: Navigation },
    { value: "restaurant", labelKey: "categoryDining", icon: Utensils },
    { value: "historical_site", labelKey: "categoryHistorical", icon: Building2 },
    { value: "nature", labelKey: "categoryNature", icon: Mountain }
  ];

  const filteredAttractions = activeCategory === "all" 
    ? attractions 
    : attractions.filter(attraction => attraction.category === activeCategory);

  const getCategoryColor = (category) => {
    const colors = {
      beach: "bg-blue-100 text-blue-800",
      restaurant: "bg-orange-100 text-orange-800",
      historical_site: "bg-purple-100 text-purple-800",
      nature: "bg-green-100 text-green-800"
    };
    return colors[category] || "bg-gray-100 text-gray-800";
  };

  const getPriceColor = (priceRange) => {
    const colors = {
      free: "bg-green-100 text-green-800",
      budget: "bg-blue-100 text-blue-800",
      moderate: "bg-yellow-100 text-yellow-800",
      expensive: "bg-red-100 text-red-800"
    };
    return colors[priceRange] || "bg-gray-100 text-gray-800";
  };

  // Helper function to get translated attraction field
  const getAttractionTranslation = (attraction, field) => {
    const langField = `${field}_${language}`;
    return attraction[langField] || attraction[field] || attraction[`${field}_en`];
  };

  const handleAttractionClick = (attractionId) => {
    // Scroll to the specific attraction card
    const attractionCard = document.getElementById(`attraction-${attractionId}`);
    if (attractionCard) {
      attractionCard.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      // Add a brief highlight effect
      attractionCard.classList.add('ring-4', 'ring-blue-500', 'ring-opacity-50');
      setTimeout(() => {
        attractionCard.classList.remove('ring-4', 'ring-blue-500', 'ring-opacity-50');
      }, 2000);
    }
  };

  // Universal map URL generator - works with any map app
  const generateMapUrl = (attraction) => {
    if (!attraction.latitude || !attraction.longitude) return null;
    
    const lat = attraction.latitude;
    const lng = attraction.longitude;
    const name = encodeURIComponent(attraction.name || 'Location');
    
    // Universal geo: URL that works on all platforms
    // iOS: Opens in Apple Maps
    // Android: Opens in Google Maps or user's preferred app
    // Desktop: Opens in default map application or browser
    return `geo:${lat},${lng}?q=${lat},${lng}(${name})`;
  };

  // Fallback for browsers that don't support geo: URLs
  const handleDirectionsClick = (attraction) => {
    const geoUrl = generateMapUrl(attraction);
    if (!geoUrl) return;

    // Try geo: URL first
    const link = document.createElement('a');
    link.href = geoUrl;
    link.click();

    // Fallback to Google Maps web if geo: doesn't work
    setTimeout(() => {
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${attraction.latitude},${attraction.longitude}`;
      window.open(googleMapsUrl, '_blank');
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center">
        <div className="absolute inset-0 z-0">
          <img
            src={anoViannosBackground}
            alt="Ano Viannos village"
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 ${ACCESSIBILITY_OVERLAYS_ENABLED ? 'bg-gradient-to-r from-gray-900/70 to-gray-800/50' : 'bg-gradient-to-r from-gray-900/35 to-gray-800/25'}`}></div>
        </div>
        
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight px-6 py-2 rounded-lg inline-block bg-black/60">
            {t('localAreaHeroTitle')}
          </h1>
          <p className="text-xl md:text-2xl text-white max-w-2xl mx-auto leading-relaxed px-6 py-3 rounded-lg font-semibold bg-black/50">
            {t('localAreaHeroSubtitle')}
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Interactive Map Section */}
        <section className="mb-16">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('localAreaMapTitle')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('localAreaMapSubtitle')}
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <InteractiveMap
              attractions={attractions}
              onAttractionClick={handleAttractionClick}
              getAttractionTranslation={getAttractionTranslation}
              height="450px"
              className="rounded-xl"
            />
          </div>
        </section>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <Button
              key={category.value}
              variant={activeCategory === category.value ? "default" : "outline"}
              onClick={() => setActiveCategory(category.value)}
              className={`flex items-center gap-2 ${
                activeCategory === category.value 
                  ? "bg-blue-600 hover:bg-blue-700" 
                  : "hover:bg-blue-50 hover:text-blue-600"
              }`}
            >
              <category.icon className="w-4 h-4" />
              {t(category.labelKey)}
            </Button>
          ))}
          
        </div>

        {/* Error State */}
        {error && (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <MapPin className="w-12 h-12 text-red-400" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-2">Error Loading Attractions</h3>
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Attractions Grid */}
        {!error && loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow-lg animate-pulse p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex gap-2">
                    <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                    <div className="h-6 bg-gray-200 rounded-full w-12"></div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded-full w-12"></div>
                </div>
                <div className="space-y-4">
                  <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  <div className="space-y-2 mt-6">
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                  </div>
                  <div className="h-10 bg-gray-200 rounded w-full mt-6"></div>
                </div>
              </div>
            ))}
          </div>
        ) : !error && filteredAttractions.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" ref={attractionsRef}>
            {filteredAttractions.map((attraction) => (
              <Card 
                key={attraction.id} 
                id={`attraction-${attraction.id}`}
                className="group border-0 shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col h-full bg-white"
              >
                <CardContent className="p-6 flex flex-col h-full">
                  {/* Header with badges and distance */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex flex-wrap gap-2">
                      <Badge className={getCategoryColor(attraction.category)}>
                        {attraction.category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Badge>
                      {attraction.price_range && (
                        <Badge className={getPriceColor(attraction.price_range)}>
                          {attraction.price_range}
                        </Badge>
                      )}
                    </div>
                    <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1 flex-shrink-0">
                      <MapPin className="w-3 h-3" />
                      {attraction.distance_km} km
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {getAttractionTranslation(attraction, 'name')}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 mb-4 line-clamp-3 flex-grow leading-relaxed">
                    {getAttractionTranslation(attraction, 'description')}
                  </p>
                  
                  {/* Details */}
                  <div className="space-y-3 mb-6">
                    {attraction.opening_hours && (
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <Clock className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <span>{attraction.opening_hours}</span>
                      </div>
                    )}
                    
                    {attraction.contact_info && (
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <Phone className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <span className="truncate">{attraction.contact_info}</span>
                      </div>
                    )}
                  </div>

                  {/* Action Button - Always at bottom */}
                  <div className="mt-auto">
                    {attraction.latitude && attraction.longitude ? (
                      <Button 
                        className="w-full bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
                        onClick={() => handleDirectionsClick(attraction)}
                      >
                        Get Directions
                        <Navigation className="ml-2 w-4 h-4" />
                      </Button>
                    ) : (
                      <Button variant="outline" className="w-full hover:bg-blue-50 hover:text-blue-600" disabled>
                        Directions Coming Soon
                        <Navigation className="ml-2 w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
              <MapPin className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-2">No attractions found</h3>
            <p className="text-gray-600">Try selecting a different category or check back later for new additions.</p>
          </div>
        )}

      </div>
    </div>
  );
}